import React, { useState } from "react";
import { Link } from "react-router-dom";

import SearchWithAPI from "../common/search-box-new";
import FileUploadComponent from "../common/FileUploadComponent.jsx";
import { FILE_KINDS, formatValue, isShown, isVisible, sectionKeyOf, settingsOf, sourceUrl } from "./custom-module-utils";
import "./custom-module.css";
import AddRowIcon from "../../assets/svg/add_new.svg";
import RemoveRowIcon from "../../assets/svg/delete-po.svg";

const INPUT_TYPES = { number: "number", date: "date", datetime: "datetime-local", email: "email", phone: "tel", url: "url" };
const GREY = "#546088";

export const inputId = (field) => `cf-${field.key}`;

const optionColor = (field, option) => field.option_colors?.[option] || GREY;

export const OptionBadge = ({ field, option }) => {
  const color = optionColor(field, option);
  return (
    <span className="fs-12 fw-600 cm-badge" style={{ color, backgroundColor: `${color}1A` }}>
      {option}
    </span>
  );
};

// "Add more" rows: one input per sub-field, a round + on the first row and a
// remove button on the others. Always shows at least one row.
const RepeaterInput = ({ field, value, onChange, id }) => {
  const rows = Array.isArray(value) && value.length ? value : [{}];
  const full = field.max_rows && rows.length >= field.max_rows;
  const setCell = (rowIndex, key, cell) => onChange(rows.map((row, i) => (i === rowIndex ? { ...row, [key]: cell } : row)));

  return (
    <div id={id} role="group" aria-label={field.label}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="d-flex align-items-center gap-2 mb-2">
          {(field.subfields ?? []).map((sub, subIndex) => (
            <div key={sub.key} style={{ flex: sub.type === "select" ? 1 : 2, minWidth: 0 }}>
              <FieldInput
                id={`${id}-${rowIndex}-${sub.key}`}
                field={{ ...sub, placeholder: `${sub.label || `Column ${subIndex + 1}`}${sub.required ? " *" : ""}` }}
                ariaLabel={`${sub.label || `Column ${subIndex + 1}`}, row ${rowIndex + 1}`}
                value={row?.[sub.key]}
                onChange={(cell) => setCell(rowIndex, sub.key, cell)}
              />
            </div>
          ))}
          {rowIndex === 0 ? (
            <button
              type="button"
              className="cm-round-btn"
              aria-label={`Add another ${field.label}`}
              title={full ? `At most ${field.max_rows}` : undefined}
              disabled={full}
              onClick={() => onChange([...rows, {}])}
            >
              <img src={AddRowIcon} alt="" />
            </button>
          ) : (
            <button
              type="button"
              className="cm-round-btn"
              aria-label={`Remove ${field.label} row ${rowIndex + 1}`}
              onClick={() => onChange(rows.filter((_, i) => i !== rowIndex))}
            >
              <img src={RemoveRowIcon} alt="" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// Attachment field: the shared drag-and-drop uploader. Each picked file is
// uploaded at once (one at a time, so every result lands on the latest list)
// and the field's value becomes the list of stored file details.
const AttachmentInput = ({ field, value, onChange, id, upload }) => {
  const [uploading, setUploading] = useState(false);
  const [problem, setProblem] = useState("");
  const files = Array.isArray(value) ? value : [];
  const kind = FILE_KINDS[field.file_kind] ?? FILE_KINDS.any;
  const maxFiles = field.max_files || 1;
  const maxMb = field.max_size_mb || 5;

  const add = async (picked, rejected) => {
    const problems = rejected.length ? [`Not allowed here or over ${maxMb} MB: ${rejected.join(", ")}.`] : [];
    if (!upload) {
      setProblem("Files can be uploaded on the real form, once the module is saved.");
      return;
    }
    const room = maxFiles - files.length;
    if (picked.length > room) problems.push(`Only ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed.`);

    setUploading(true);
    let next = files;
    for (const file of picked.slice(0, Math.max(room, 0))) {
      const response = await upload(field.key, file.name, file.base64);
      if (response?.data?.success) {
        next = [...next, response.data.file];
        onChange(next);
      } else {
        problems.push(response?.data?.message ?? `${file.name}: upload failed.`);
      }
    }
    setUploading(false);
    setProblem(problems.join(" "));
  };

  return (
    <div id={id}>
      <FileUploadComponent
        uploadedFiles={files.map((file) => ({ name: file.name, size: file.size, type: file.type, base64: file.url }))}
        onFilesAdd={add}
        onFileRemove={(index) => onChange(files.filter((_, i) => i !== index))}
        maxSize={maxMb}
        acceptedFormats={kind.exts.map((ext) => `.${ext}`).join(",")}
      />
      <div className="fs-12 fw-500 text-grey mt-1">
        {kind.label} · up to {maxFiles} file{maxFiles > 1 ? "s" : ""}, {maxMb} MB each
      </div>
      {uploading && <div className="fs-12 fw-500 text-grey" role="status">Uploading…</div>}
      {problem && <div className="fs-12" role="alert" style={{ color: "#ff4444" }}>{problem}</div>}
    </div>
  );
};

// One input for any field type; `onChange` receives the new value, not an event.
// `ariaLabel` names inputs that have no visible label (repeater cells);
// `upload` sends an attachment file (absent in the builder preview).
export const FieldInput = ({ field, value, onChange, id = inputId(field), ariaLabel, upload }) => {
  const placeholder = field.placeholder || undefined;

  switch (field.type) {
    case "attachment":
      return <AttachmentInput field={field} value={value} onChange={onChange} id={id} upload={upload} />;
    case "repeater":
      return <RepeaterInput field={field} value={value} onChange={onChange} id={id} />;
    case "textarea":
      return (
        <textarea
          id={id}
          aria-label={ariaLabel}
          className="form-control"
          rows={3}
          value={value ?? ""}
          maxLength={field.max_length || undefined}
          placeholder={placeholder ?? "Enter here..."}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "select":
      return (
        <select id={id} aria-label={ariaLabel} className="form-control" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">{placeholder ?? "Select"}</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    case "multiselect": {
      const picked = Array.isArray(value) ? value : [];
      return (
        <div id={id} className="d-flex flex-wrap gap-2" role="group" aria-label={field.label}>
          {(field.options ?? []).map((option) => {
            const on = picked.includes(option);
            const color = optionColor(field, option);
            return (
              <button
                key={option}
                type="button"
                className="fs-13 fw-500 cm-chip"
                aria-pressed={on}
                style={on ? { background: color, borderColor: color, color: "#fff" } : undefined}
                onClick={() => onChange(on ? picked.filter((item) => item !== option) : [...picked, option])}
              >
                {option}
              </button>
            );
          })}
        </div>
      );
    }
    case "checkbox":
      return (
        <div className="form-check">
          <input id={id} type="checkbox" className="form-check-input" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        </div>
      );
    case "autocomplete":
      return (
        <div className="d-flex align-items-center gap-2">
          <div style={{ flex: 1 }}>
            <SearchWithAPI
              apiUrl={sourceUrl(field.source) ?? ""}
              placeholder={placeholder ?? "Select"}
              selectedOption={value?.value ? value : null}
              onDropdownChange={(option) => onChange({ value: option.value, label: option.label })}
            />
          </div>
          {value?.value && !field.required && (
            <button type="button" className="btn btn-link p-0 fs-13" onClick={() => onChange(null)}>
              Clear
            </button>
          )}
        </div>
      );
    default:
      return (
        <input
          id={id}
          aria-label={ariaLabel}
          className="form-control"
          type={INPUT_TYPES[field.type] ?? "text"}
          value={value ?? ""}
          min={field.type === "number" && field.min !== "" ? field.min ?? undefined : undefined}
          max={field.type === "number" && field.max !== "" ? field.max ?? undefined : undefined}
          step={field.type === "number" ? "any" : undefined}
          maxLength={field.max_length || undefined}
          placeholder={placeholder ?? (INPUT_TYPES[field.type] === "date" || field.type === "datetime" ? undefined : "Enter here...")}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

// A stored value for reading: option badges, live links, formatted dates.
// Links stop the click so a clickable list row underneath does not also fire.
export const FieldValue = ({ field, value }) => {
  const stop = (e) => e.stopPropagation();

  if (field.type === "select" && value) return <OptionBadge field={field} option={value} />;
  if (field.type === "multiselect" && Array.isArray(value) && value.length) {
    return (
      <span className="d-inline-flex flex-wrap gap-1">
        {value.map((option) => <OptionBadge key={option} field={field} option={option} />)}
      </span>
    );
  }
  if (field.type === "url" && /^https?:\/\//i.test(value ?? "")) {
    return <a className="textblue" href={value} target="_blank" rel="noreferrer" onClick={stop}>{value}</a>;
  }
  if (field.type === "email" && value) {
    return <a className="textblue" href={`mailto:${value}`} onClick={stop}>{value}</a>;
  }
  if (field.type === "autocomplete" && field.source?.startsWith("module:") && value?.value) {
    return <Link className="textblue" to={`/custom/${field.source.slice(7)}/view/${value.value}`} onClick={stop}>{value.label}</Link>;
  }
  if (field.type === "attachment" && Array.isArray(value) && value.length) {
    // Links only where the server signed one (the view page); names elsewhere.
    return value.map((file, index) => (
      <span key={file.path ?? index}>
        {index > 0 && ", "}
        {file.url ? (
          <a className="textblue" href={file.url} target="_blank" rel="noreferrer" onClick={stop}>{file.name}</a>
        ) : (
          file.name
        )}
      </span>
    ));
  }
  if (field.type === "repeater" && Array.isArray(value) && value.length) {
    // Rows inline, so a list cell can still cut them off with an ellipsis.
    return value.map((row, rowIndex) => (
      <span key={rowIndex}>
        {rowIndex > 0 && ", "}
        {(field.subfields ?? [])
          .filter((sub) => ![null, undefined, ""].includes(row?.[sub.key]))
          .map((sub, cellIndex) => (
            <span key={sub.key}>
              {cellIndex > 0 && " · "}
              <FieldValue field={sub} value={row[sub.key]} />
            </span>
          ))}
      </span>
    ));
  }
  return formatValue(field, value);
};

const visibleFieldsBySection = (module, values) => {
  const { sections } = settingsOf(module);
  return sections
    .map((section) => ({
      section,
      fields: module.fields.filter(
        (field) => isShown(field) && sectionKeyOf(field, sections) === section.key && isVisible(field, module.fields, values)
      ),
    }))
    .filter((group) => group.fields.length);
};

const span = (field) => (field.width === "full" ? { gridColumn: "1 / -1" } : undefined);

// The create/edit form laid out by the module's sections, columns and widths;
// fields hidden by a "show only when" rule drop out as values change.
export const RecordForm = ({ module, values, onChange, errors = {}, upload }) =>
  visibleFieldsBySection(module, values).map(({ section, fields }) => (
    <div key={section.key} className="p-4 mb-3 cm-card">
      {section.title && <div className="fs-16 fw-600 text-dkblue mb-3">{section.title}</div>}
      <div className="cm-grid" style={{ gridTemplateColumns: `repeat(${section.columns}, minmax(0, 1fr))` }}>
        {fields.map((field) => {
          const error = errors[field.key] || errors[`${field.key}.value`] || Object.entries(errors).find(([key]) => key.startsWith(`${field.key}.`))?.[1];
          return (
            <div key={field.key} className={`mb-3 ${field.type === "repeater" ? "cm-repeat-card" : ""}`} style={span(field)}>
              <label htmlFor={inputId(field)} className={field.type === "repeater" ? "fs-13 fw-600 text-dkblue mb-2" : "fs-14 fw-500 text-grey mb-1"}>
                {field.label}
                {field.required && (field.type === "repeater" ? <span style={{ color: "#ff4444" }}> *</span> : " *")}
              </label>
              <FieldInput field={field} value={values[field.key]} upload={upload} onChange={(value) => onChange(field.key, value)} />
              {field.help && <div className="fs-12 fw-500 text-grey mt-1">{field.help}</div>}
              {error && <div className="fs-12" style={{ color: "#ff4444" }}>{error}</div>}
            </div>
          );
        })}
      </div>
    </div>
  ));

// The view page's read-only counterpart of RecordForm.
export const RecordDetails = ({ module, values }) =>
  visibleFieldsBySection(module, values).map(({ section, fields }) => (
    <div key={section.key} className="iv-card mb-3">
      <div className="iv-card-title">{section.title || `${module.name} Details`}</div>
      <div className="cm-grid" style={{ gridTemplateColumns: `repeat(${section.columns}, minmax(0, 1fr))`, rowGap: "16px" }}>
        {fields.map((field) => (
          <div key={field.key} style={span(field)}>
            <div className="iv-detail-label">{field.label}</div>
            <div className="iv-detail-value cm-prewrap">
              <FieldValue field={field} value={values[field.key]} />
            </div>
          </div>
        ))}
      </div>
    </div>
  ));
